import { Box, TextField, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid";
import React, { useMemo, useState } from "react";
import { brand } from "../../../shared/theme/app-theme";
import {
  DashboardStudentStatsDTO,
  UniversityStudentStatsDTO,
} from "../model";

type Props = {
  data?: DashboardStudentStatsDTO;
  periodLabel: string;
};

function formatCount(value: number) {
  return (value ?? 0).toLocaleString("ru-RU");
}

const countColumn = (
  field: keyof UniversityStudentStatsDTO,
  headerName: string
): GridColDef<UniversityStudentStatsDTO> => ({
  field,
  headerName,
  width: 110,
  type: "number",
  align: "right",
  headerAlign: "right",
  valueFormatter: (params) => formatCount(Number(params.value ?? 0)),
});

const emptyStats = {
  mnvoTotal: 0,
  mzTotal: 0,
  mnvoAssigned: 0,
  mzAssigned: 0,
  mnvoUnassigned: 0,
  mzUnassigned: 0,
};

const UniversityStudentStatsTable: React.FC<Props> = ({ data, periodLabel }) => {
  const [search, setSearch] = useState("");
  const rows = data?.universities ?? [];

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return rows;
    }
    return rows.filter((row) =>
      row.universityName.toLowerCase().includes(query)
    );
  }, [rows, search]);

  const total = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => ({
          mnvoTotal: acc.mnvoTotal + (row.mnvoTotal ?? 0),
          mzTotal: acc.mzTotal + (row.mzTotal ?? 0),
          mnvoAssigned: acc.mnvoAssigned + (row.mnvoAssigned ?? 0),
          mzAssigned: acc.mzAssigned + (row.mzAssigned ?? 0),
          mnvoUnassigned: acc.mnvoUnassigned + (row.mnvoUnassigned ?? 0),
          mzUnassigned: acc.mzUnassigned + (row.mzUnassigned ?? 0),
        }),
        emptyStats
      ),
    [filteredRows]
  );

  const columns = useMemo<GridColDef<UniversityStudentStatsDTO>[]>(
    () => [
      {
        field: "universityName",
        headerName: "ВУЗ",
        flex: 1,
        minWidth: 260,
      },
      countColumn("mnvoTotal", "МНВО"),
      countColumn("mzTotal", "МЗ"),
      countColumn("mnvoAssigned", "МНВО"),
      countColumn("mzAssigned", "МЗ"),
      countColumn("mnvoUnassigned", "МНВО"),
      countColumn("mzUnassigned", "МЗ"),
    ],
    []
  );

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: "Всего",
      headerAlign: "center",
      children: [{ field: "mnvoTotal" }, { field: "mzTotal" }],
    },
    {
      groupId: "Назначена стипендия",
      headerAlign: "center",
      children: [{ field: "mnvoAssigned" }, { field: "mzAssigned" }],
    },
    {
      groupId: "Без назначенной стипендии",
      headerAlign: "center",
      children: [{ field: "mnvoUnassigned" }, { field: "mzUnassigned" }],
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {periodLabel}. МНВО — 204-100 и 204-118, МЗ — 324-006. «Назначена» —
        заполнен номер приказа о назначении стипендии.
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <SummaryCard
          title="Всего"
          mnvo={total?.mnvoTotal ?? 0}
          mz={total?.mzTotal ?? 0}
        />
        <SummaryCard
          title="Назначена стипендия"
          mnvo={total?.mnvoAssigned ?? 0}
          mz={total?.mzAssigned ?? 0}
        />
        <SummaryCard
          title="Без назначенной стипендии"
          mnvo={total?.mnvoUnassigned ?? 0}
          mz={total?.mzUnassigned ?? 0}
        />
      </Box>
      <TextField
        size="small"
        label="Поиск вуза"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ width: 280, maxWidth: "100%" }}
      />
      <DataGrid
        autoHeight
        density="compact"
        rows={filteredRows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        getRowId={(row) =>
          row.universityId != null ? String(row.universityId) : row.universityName
        }
        disableRowSelectionOnClick
        disableColumnMenu
        pageSizeOptions={[10, 20, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 20, page: 0 } },
        }}
        sx={{
          "& .MuiDataGrid-cell": { py: 0 },
          "& .MuiDataGrid-columnHeader": { py: 0 },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
          "& .MuiDataGrid-footerContainer": { minHeight: 40 },
        }}
      />
      <Typography sx={{ fontSize: 13, color: brand.muted }}>
        Показано {filteredRows.length} из {rows.length}
      </Typography>
    </Box>
  );
};

function SummaryCard({
  title,
  mnvo,
  mz,
}: {
  title: string;
  mnvo: number;
  mz: number;
}) {
  return (
    <Box
      sx={{
        flex: "1 1 220px",
        border: `1px solid ${brand.line}`,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        bgcolor: brand.white,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        МНВО {formatCount(mnvo)} · МЗ {formatCount(mz)}
      </Typography>
    </Box>
  );
}

export default UniversityStudentStatsTable;
